import React, { useCallback, useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
const images = [{
  src: 'https://cdn.magicpatterns.com/uploads/cHJ8gKjKkL8mDqjrSacnd6/IMG_4494.jpg',
  alt: 'Historický pokoj s lustrem'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/kUyKmd1Ts2QeFtMtYHGkwn/IMG_4088.jpg',
  alt: 'Zdobený strop s freskami'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/25k894Z1RN1JyEcxFVGo24/IMG_4065-2.jpg',
  alt: 'Schodiště s historickou malbou'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/p895FZ1ENpM2QyZUh39PBp/ResidenceMalostranska-7112.jpg',
  alt: 'Pohled na kostel sv. Mikuláše'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/iveNmcCVRpk9kf95zJq3rS/IMG_4060.jpg',
  alt: 'Building entrance with mosaic floor and marble columns'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/5MPCq8HAJfj6GF1yEkDezJ/IMG_4477.jpg',
  alt: 'Bedroom with crystal chandelier and Prague artwork'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/aF9Jo3VXVh4JjJxhfBkohc/IMG_4501.jpg',
  alt: 'Bedroom with golden curtains and sitting area'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/oGnwLyVQCFLrRQwDMG6Phy/IMG_4502.jpg',
  alt: 'Bedroom with ornate gold bed frame'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/47DZApMjFksDEb8tJhp5BY/IMG_4510.jpg',
  alt: 'Hallway with vintage chandelier and Prague paintings'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/qh9RdvcfaPcyrACtKu5ktM/IMG_4486.jpg',
  alt: 'Obývací pokoj s lustrem a parketami'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/8YwXAZH7wzvWpGencCPnQv/IMG_4490.jpg',
  alt: 'Salon se zlatými závěsy a pohovkou'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/q9EfujNcLK8uuiSd31zqCh/IMG_4492.jpg',
  alt: 'Ložnice s historickými fotografiemi Prahy'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/fwHUNTW9Ka8A45b9ZbXCK4/IMG_4493.jpg',
  alt: 'Ložnice s křišťálovým lustrem a secesním nábytkem'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/1zLwpDxFZDnZLwM3PCyq9U/IMG_4500.jpg',
  alt: 'Hlavní ložnice se zlatým rámem postele'
}];
export function GallerySection() {
  const {
    t
  } = useLanguage();
  const scrollRef = useRef<HTMLDivElement>(null);
  const scrollSpeedRef = useRef(0);
  const animationFrameRef = useRef<number | null>(null);
  const [lightbox, setLightbox] = useState({
    isOpen: false,
    currentIndex: 0
  });
  const openLightbox = (index: number) => {
    setLightbox({
      isOpen: true,
      currentIndex: index
    });
  };
  const closeLightbox = useCallback(() => {
    setLightbox(prev => ({
      ...prev,
      isOpen: false
    }));
  }, []);
  const nextImage = useCallback((e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setLightbox(prev => ({
      ...prev,
      currentIndex: (prev.currentIndex + 1) % images.length
    }));
  }, []);
  const prevImage = useCallback((e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setLightbox(prev => ({
      ...prev,
      currentIndex: (prev.currentIndex - 1 + images.length) % images.length
    }));
  }, []);
  // Hover-to-scroll logic
  const startScrolling = useCallback((direction: 'left' | 'right') => {
    scrollSpeedRef.current = direction === 'left' ? -8 : 8;
    const animate = () => {
      if (scrollRef.current && scrollSpeedRef.current !== 0) {
        scrollRef.current.scrollLeft += scrollSpeedRef.current;
        animationFrameRef.current = requestAnimationFrame(animate);
      }
    };
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    animationFrameRef.current = requestAnimationFrame(animate);
  }, []);
  const stopScrolling = useCallback(() => {
    scrollSpeedRef.current = 0;
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
  }, []);
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!lightbox.isOpen) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') prevImage();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightbox.isOpen, closeLightbox, nextImage, prevImage]);
  // Prevent scrolling when lightbox is open
  useEffect(() => {
    if (lightbox.isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [lightbox.isOpen]);
  return <section id="galerie" className="py-24 bg-charcoal-900 relative overflow-hidden bg-grain" data-id="element-77">
      <div className="max-w-7xl mx-auto px-6 md:px-12 mb-12" data-id="element-78">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="flex items-center gap-4" data-id="element-79">
          <span className="tracking-[0.2em] uppercase text-gold font-semibold text-[18px]" data-id="element-80">
            {t.gallery.title}
          </span>
          <div className="flex-1 h-[1px] bg-charcoal-800" data-id="element-81"></div>
        </motion.div>
      </div>

      <div className="relative w-full" data-id="element-82">
        <div ref={scrollRef} className="flex overflow-x-auto gap-8 px-6 md:px-12 pb-12 pt-4 hide-scrollbar cursor-grab active:cursor-grabbing" style={{
        scrollBehavior: 'auto'
      }} data-id="element-83">
          {images.map((image, index) => <motion.div key={index} initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true,
          margin: '-50px'
        }} transition={{
          duration: 0.6,
          delay: index * 0.1
        }} className="relative flex-none w-[85vw] md:w-[60vw] lg:w-[40vw] aspect-[4/3] group cursor-pointer" onClick={() => openLightbox(index)} data-id="element-84">
              <div className="absolute inset-0 border border-gold/20 transition-colors duration-500 group-hover:border-gold/60 z-10 pointer-events-none m-4" data-id="element-85"></div>
              <img src={image.src} alt={image.alt} className="w-full h-full object-cover brightness-75 group-hover:brightness-100 transition-all duration-700" draggable="false" data-id="element-86" />
            </motion.div>)}
        </div>

        {/* Hover scroll zones + Fade edges */}
        <div className="absolute top-0 bottom-0 left-0 w-24 md:w-40 bg-gradient-to-r from-charcoal-900 to-transparent z-10 cursor-w-resize" onMouseEnter={() => startScrolling('left')} onMouseLeave={stopScrolling} data-id="element-87"></div>
        <div className="absolute top-0 bottom-0 right-0 w-24 md:w-40 bg-gradient-to-l from-charcoal-900 to-transparent z-10 cursor-e-resize" onMouseEnter={() => startScrolling('right')} onMouseLeave={stopScrolling} data-id="element-88"></div>
      </div>

      {/* Lightbox Modal */}
      <AnimatePresence data-id="element-89">
        {lightbox.isOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4 md:p-8" onClick={closeLightbox} data-id="element-90">
            <button onClick={closeLightbox} className="absolute top-6 right-6 text-cream/70 hover:text-gold transition-colors z-50 p-2" aria-label={t.gallery.close} data-id="element-91">
              <X className="w-8 h-8" data-id="element-92" />
            </button>

            <button onClick={prevImage} className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 text-cream/70 hover:text-gold transition-colors z-50 p-2" aria-label={t.gallery.prev} data-id="element-93">
              <ChevronLeft className="w-10 h-10 md:w-12 md:h-12" data-id="element-94" />
            </button>

            <button onClick={nextImage} className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 text-cream/70 hover:text-gold transition-colors z-50 p-2" aria-label={t.gallery.next} data-id="element-95">
              <ChevronRight className="w-10 h-10 md:w-12 md:h-12" data-id="element-96" />
            </button>

            <div className="relative w-full max-w-5xl h-[85vh] flex items-center justify-center" onClick={e => e.stopPropagation()} data-id="element-97">
              <AnimatePresence mode="wait" data-id="element-98">
                <motion.img key={lightbox.currentIndex} src={images[lightbox.currentIndex].src} alt={images[lightbox.currentIndex].alt} initial={{
              opacity: 0,
              scale: 0.98
            }} animate={{
              opacity: 1,
              scale: 1
            }} exit={{
              opacity: 0,
              scale: 1.02
            }} transition={{
              duration: 0.3
            }} className="max-w-full max-h-full object-contain" data-id="element-99" />
              </AnimatePresence>
            </div>

            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-cream/70 font-light tracking-widest text-sm" data-id="element-100">
              {lightbox.currentIndex + 1} / {images.length}
            </div>
          </motion.div>}
      </AnimatePresence>
    </section>;
}